/**
 * The pre-painted motif: an original pinwheel of tapered petals and paired arrows, designed for
 * the rotation group of {7,3} and drawn with the same strokes a visitor paints with (so it can
 * be erased, undone and shared like any other motif).
 *
 * Shapes are authored in "wedge coordinates" of the rotation domain W: u ∈ [−1, 1] is the angle
 * as a fraction of π/p (u = ±1 on the straight sides through the tile centre P), v ∈ [0, 1] the
 * hyperbolic distance from P as a fraction of the distance to the far side along that ray.
 */
import type { Complex } from '../geometry/complex';
import { type TriangleGroup, buildTriangleGroup } from '../geometry/triangle-group';
import { toDomain } from './domain';
import {
  type Motif,
  ROLE_GOLD,
  ROLE_INK,
  ROLE_IVORY,
  ROLE_VERMILION,
  type Stroke,
  type StrokeRole,
} from './motif';

/** Point of W at wedge coordinates (u, v). */
export function wedgePoint(tg: TriangleGroup, u: number, v: number): Complex {
  const theta = (u * Math.PI) / tg.p;
  const c = theta >= 0 ? tg.center : { re: tg.center.re, im: -tg.center.im };
  const m = c.re * Math.cos(theta) + c.im * Math.sin(theta);
  const rEdge = m - Math.sqrt(Math.max(m * m - 1, 0)); // smaller root of r² − 2rm + 1 = 0
  const dEdge = Math.log((1 + rEdge) / (1 - rEdge));
  const r = Math.tanh((v * dEdge) / 2);
  return { re: r * Math.cos(theta), im: r * Math.sin(theta) };
}

type Key = [u: number, v: number, w?: number];

/** Catmull–Rom interpolation through key points (with optional width keys). */
function spline(keys: Key[], samples: number): Key[] {
  const out: Key[] = [];
  const get = (i: number) => keys[Math.max(0, Math.min(keys.length - 1, i))];
  for (let i = 0; i < keys.length - 1; i++) {
    const p0 = get(i - 1);
    const p1 = get(i);
    const p2 = get(i + 1);
    const p3 = get(i + 2);
    for (let s = 0; s < samples; s++) {
      const t = s / samples;
      const t2 = t * t;
      const t3 = t2 * t;
      const f = (a: number, b: number, c: number, d: number) =>
        0.5 *
        (2 * b + (-a + c) * t + (2 * a - 5 * b + 4 * c - d) * t2 + (-a + 3 * b - 3 * c + d) * t3);
      out.push([
        f(p0[0], p1[0], p2[0], p3[0]),
        f(p0[1], p1[1], p2[1], p3[1]),
        f(p0[2] ?? 1, p1[2] ?? 1, p2[2] ?? 1, p3[2] ?? 1),
      ]);
    }
  }
  const last = keys[keys.length - 1];
  out.push([last[0], last[1], last[2] ?? 1]);
  return out;
}

function stroke(
  tg: TriangleGroup,
  role: StrokeRole,
  size: number,
  keys: Key[],
  samples = 8,
): Stroke {
  const pts = keys.length > 2 ? spline(keys, samples) : keys;
  const tapered = pts.some((k) => (k[2] ?? 1) !== 1);
  return {
    role,
    symmetry: 'rotation',
    size,
    points: pts.map(([u, v]) => toDomain(tg, wedgePoint(tg, u, v))),
    widths: tapered ? pts.map((k) => Math.max(0.05, Math.min(1, k[2] ?? 1))) : undefined,
  };
}

/** Builds the default motif (in domain coordinates of the {7,3} rotation domain). */
export function defaultMotif(): Motif {
  const tg = buildTriangleGroup(7, 3, 2);
  const ring = (v: number): Key[] => [
    [-1.05, v],
    [-0.5, v],
    [0, v],
    [0.5, v],
    [1.05, v],
  ];
  const dot = (u: number, v: number): Key[] => [
    [u, v],
    [u + 0.01, v],
  ];
  const s: Stroke[] = [];
  // Petal: an ink blade sweeping from near the centre out toward the vertex Q. Seven of them
  // turn around every tile; three tips meet at every vertex.
  s.push(
    stroke(tg, ROLE_INK, 0.4, [
      [-0.55, 0.3, 0.12],
      [-0.25, 0.42, 0.7],
      [0.15, 0.56, 1],
      [0.5, 0.7, 0.8],
      [0.78, 0.86, 0.4],
      [0.92, 0.97, 0.08],
    ]),
  );
  // Ivory vein along the petal.
  s.push(
    stroke(tg, ROLE_IVORY, 0.07, [
      [-0.32, 0.4, 0.3],
      [0.05, 0.53, 1],
      [0.42, 0.67, 1],
      [0.7, 0.8, 0.3],
    ]),
  );
  // Vermilion swash through the edge midpoint R. The half-turn about R completes it into an S
  // that crosses the tile edge, so neighbouring tiles hook into each other.
  s.push(
    stroke(tg, ROLE_VERMILION, 0.15, [
      [0.0, 1.06, 0.6],
      [-0.12, 0.92, 1],
      [-0.34, 0.8, 0.9],
      [-0.58, 0.72, 0.55],
      [-0.78, 0.7, 0.12],
    ]),
  );
  // Gold rosette: seven arcs close into a heptagonal ring around a gold seed.
  s.push(stroke(tg, ROLE_GOLD, 0.1, ring(0.2), 6));
  s.push(stroke(tg, ROLE_GOLD, 0.2, dot(0, 0), 1));
  // A fine vermilion ring framing the rosette.
  s.push(stroke(tg, ROLE_VERMILION, 0.035, ring(0.29), 6));
  // Gold bead where three petal tips meet.
  s.push(stroke(tg, ROLE_GOLD, 0.11, dot(0.62, 0.9), 1));
  return { strokes: s };
}
